import { Check, Copy, Terminal } from 'lucide-react'
import { useState } from 'react'

interface StepInstallSdkProps {
  onNext: () => void
  onSkip: () => void
}

const LANGUAGES = ['TypeScript', 'Python', 'Go', 'Rust'] as const
type Language = (typeof LANGUAGES)[number]

interface LanguageConfig {
  install: string
  example: string
}

const SDK_CONFIG: Record<Language, LanguageConfig> = {
  TypeScript: {
    install: 'npm install one-email',
    example: `import { OneEmail } from 'one-email'

const client = new OneEmail('oe_live_sk_...')

await client.emails.send({
  from: 'you@yourapp.com',
  to: ['user@example.com'],
  subject: 'Hello',
  html: '<p>Welcome!</p>'
})`,
  },
  Python: {
    install: 'pip install one-email',
    example: `from one_email import OneEmail

client = OneEmail("oe_live_sk_...")

client.emails.send(
    from_address="you@yourapp.com",
    to=["user@example.com"],
    subject="Hello",
    html="<p>Welcome!</p>"
)`,
  },
  Go: {
    install: 'go get github.com/one-email/one-email-go',
    example: `package main

import oe "github.com/one-email/one-email-go"

func main() {
    client := oe.New("oe_live_sk_...")

    client.Emails.Send(&oe.SendParams{
        From:    "you@yourapp.com",
        To:      []string{"user@example.com"},
        Subject: "Hello",
        HTML:    "<p>Welcome!</p>",
    })
}`,
  },
  Rust: {
    install: 'cargo add one-email',
    example: `use one_email::{OneEmail, SendEmailRequest};

let client = OneEmail::new("oe_live_sk_...").unwrap();

client.emails().send(SendEmailRequest {
    from: "you@yourapp.com".into(),
    to: vec!["user@example.com".into()],
    subject: "Hello".into(),
    html: Some("<p>Welcome!</p>".into()),
    ..Default::default()
}).await?;`,
  },
}

function CopyBtn({ text }: { text: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false)

  function handleCopy(): void {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 rounded-md p-1.5 text-text-tertiary hover:text-text-secondary hover:bg-surface-2 transition-colors"
      aria-label="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

export function StepInstallSdk({ onNext, onSkip }: StepInstallSdkProps): React.JSX.Element {
  const [lang, setLang] = useState<Language>('TypeScript')
  const config = SDK_CONFIG[lang]

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50">
          <Terminal className="h-5 w-5 text-brand-500" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Install the SDK</h2>
          <p className="text-sm text-text-secondary">Get started in under a minute.</p>
        </div>
      </div>

      {/* Language tabs */}
      <div className="flex gap-1 rounded-lg bg-surface-1 p-1 border border-surface-3">
        {LANGUAGES.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLang(l)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              l === lang
                ? 'bg-surface-0 text-text-primary shadow-sm'
                : 'text-text-tertiary hover:text-text-secondary'
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Install command */}
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-tertiary">
          Install
        </p>
        <div className="flex items-center justify-between rounded-lg border border-surface-3 bg-surface-0 px-4 py-3">
          <code className="font-mono text-sm text-text-primary">{config.install}</code>
          <CopyBtn text={config.install} />
        </div>
      </div>

      {/* Example */}
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-tertiary">
          Quick example
        </p>
        <div className="relative rounded-lg border border-surface-3 bg-surface-0">
          <div className="absolute right-2 top-2">
            <CopyBtn text={config.example} />
          </div>
          <pre className="overflow-x-auto p-4 font-mono text-sm text-text-primary leading-relaxed">
            {config.example}
          </pre>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onSkip}
          className="text-sm font-medium text-text-tertiary hover:text-text-secondary transition-colors"
        >
          Skip for now
        </button>
        <button
          type="button"
          onClick={onNext}
          className="rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600 transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
